import { Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { AttachMoney } from '@mui/icons-material';
import dayjs from 'dayjs';
import FormHeading from 'common/FormHeading';
import FormInputLabel from 'common/FormInputLabel';
import FormModal from 'common/FormModal';
import FormSelectInput from 'common/FormSelectInput';
import FormStyle from 'common/FormStyle';
import FormTextInput from 'common/FormTextInput';
import StickyActionFooter from 'common/StickyActionFooter';
import { getLocationForBusinessDataApi } from 'SupabseHandler';
import AddLocationPopup from 'pages/Business/Location/AddLocationPopup';
import { theme } from 'theme';
import { generateTimeOptions } from 'utils/time';
import { toasterNotification } from 'utils/toaster';

import { useAuth } from 'context/AuthContext';

import {
    businessData as businessDataAtom,
    locationDataList,
    selectedLocation as selectedLocationAtom,
} from 'atoms';
import { useRecoilState } from 'recoil';

import React, { useEffect, useMemo, useRef, useState } from 'react';

const WEEK_DAYS = [
    { id: 1, day: 'Monday' },
    { id: 2, day: 'Tuesday' },
    { id: 3, day: 'Wednesday' },
    { id: 4, day: 'Thursday' },
    { id: 5, day: 'Friday' },
    { id: 6, day: 'Saturday' },
    { id: 7, day: 'Sunday' },
];

const VISIT_MODE_OPTIONS = [
    { id: 'both', day: 'In-Person & Telemedicine' },
    { id: 'in_person', day: 'In-Person Only' },
    { id: 'telemedicine', day: 'Telemedicine Only' },
];

const formatTimeLabel = (value) => {
    if (!value) return '-';
    if (typeof value === 'string' && /^\d{2}:\d{2}/.test(value)) {
        const parsed = dayjs(`2000-01-01T${value.slice(0, 5)}`);
        if (parsed.isValid()) return parsed.format('h:mm A');
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('h:mm A') : String(value);
};

const dayLabelFromValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const found = WEEK_DAYS.find((d) => String(d.id) === String(value));
    return found?.day || String(value);
};

const visitModeFromRow = (row) => {
    if (row?.visit_mode) return row.visit_mode;
    const isInPerson = row?.is_inperson === true || row?.is_inperson === '1' || row?.is_inperson === 1;
    const isTele = row?.is_telemedicine === true || row?.is_telemedicine === '1' || row?.is_telemedicine === 1;
    if (isInPerson && isTele) return 'both';
    if (isTele) return 'telemedicine';
    return 'in_person';
};

const visitModeLabel = (mode) => VISIT_MODE_OPTIONS.find((opt) => opt.id === mode)?.day || mode;

const emptyForm = {
    day: '',
    start_time: '',
    end_time: '',
    visit_mode: 'in_person',
    location: '',
};

const AvailabilityPopup = ({
    open,
    onClose,
    businessId,
    availabilityList = [],
    selectedAvailability = null,
    refreshList,
    onDelete,
}) => {
    const { token } = useAuth();
    const [businessData, setBusinessData] = useRecoilState(businessDataAtom);
    const [, setLocationFormData] = useRecoilState(locationDataList);
    const [, setSelectedLocation] = useRecoilState(selectedLocationAtom);
    const timeSlots = useMemo(() => generateTimeOptions(15), []);

    const [addInPerson, setAddInPerson] = useState(false);
    const [addTelemedicine, setAddTelemedicine] = useState(false);
    const [inPersonPrice, setInPersonPrice] = useState('');
    const [telemedicinePrice, setTelemedicinePrice] = useState('');
    const [consultationPriceErrors, setConsultationPriceErrors] = useState({ inPerson: false, telemedicine: false });
    const [savingDone, setSavingDone] = useState(false);
    const [doneError, setDoneError] = useState('');

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [formError, setFormError] = useState('');

    const doneInProgressRef = useRef(false);

    const [locations, setLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [openLocationPopup, setOpenLocationPopup] = useState(false);
    const [locationCountBeforeAdd, setLocationCountBeforeAdd] = useState(0);

    const fetchLocations = async () => {
        if (!businessData?.id || !token) {
            setLocations([]);
            return [];
        }
        setLoadingLocations(true);
        try {
            const data = await getLocationForBusinessDataApi(businessData.id, token);
            const list = Array.isArray(data) ? data : data ? [data] : [];
            const mapped = list.map((d) => ({
                id: d.id,
                location: [d.name, d.address, d.city, d.state, d.suite, d.zip].filter(Boolean).join(' '),
            }));
            setLocations(mapped);
            return mapped;
        } catch (err) {
            console.error('Error fetching locations:', err);
            setLocations([]);
            return [];
        } finally {
            setLoadingLocations(false);
        }
    };

    useEffect(() => {
        if (!open) {
            setAddInPerson(false);
            setAddTelemedicine(false);
            setInPersonPrice('');
            setTelemedicinePrice('');
            setConsultationPriceErrors({ inPerson: false, telemedicine: false });
            setSavingDone(false);
            setDoneError('');
            setShowForm(false);
            setEditingId(null);
            setFormData(emptyForm);
            setFormError('');
            return;
        }

        fetchLocations();

        // Prefill consultation checkboxes/prices from saved business data
        const savedInPersonPrice = businessData?.in_person_consultation_price;
        const savedTelemedicinePrice = businessData?.telemedicine_consultation_price;
        const hasInPerson = savedInPersonPrice !== undefined && savedInPersonPrice !== null && savedInPersonPrice !== '' && Number(savedInPersonPrice) > 0;
        const hasTelemedicine = savedTelemedicinePrice !== undefined && savedTelemedicinePrice !== null && savedTelemedicinePrice !== '' && Number(savedTelemedicinePrice) > 0;
        setAddInPerson(hasInPerson);
        setAddTelemedicine(hasTelemedicine);
        setInPersonPrice(hasInPerson ? Number(savedInPersonPrice).toFixed(2) : '');
        setTelemedicinePrice(hasTelemedicine ? Number(savedTelemedicinePrice).toFixed(2) : '');

        if (selectedAvailability && selectedAvailability.id) {
            // Open in edit mode prefilled from the row
            setEditingId(selectedAvailability.id);
            setShowForm(true);
            setFormData({
                day: selectedAvailability.day !== undefined && selectedAvailability.day !== null
                    ? String(selectedAvailability.day)
                    : '',
                start_time: (selectedAvailability.start_time || selectedAvailability.start_datetime || '').slice(0, 5),
                end_time: (selectedAvailability.end_time || selectedAvailability.end_datetime || '').slice(0, 5),
                visit_mode: visitModeFromRow(selectedAvailability),
                location: selectedAvailability.location !== undefined && selectedAvailability.location !== null
                    ? String(selectedAvailability.location)
                    : '',
            });
        }
    }, [open, selectedAvailability]);

    const handleToggleInPerson = () => {
        setAddInPerson((prev) => {
            const next = !prev;
            if (!next) {
                setInPersonPrice('');
                setConsultationPriceErrors((p) => ({ ...p, inPerson: false }));
            }
            return next;
        });
    };
    const handleToggleTelemedicine = () => {
        setAddTelemedicine((prev) => {
            const next = !prev;
            if (!next) {
                setTelemedicinePrice('');
                setConsultationPriceErrors((p) => ({ ...p, telemedicine: false }));
            }
            return next;
        });
    };

    const handlePriceChange = (e, key) => {
        const { value } = e.target;
        const numericValue = String(value).replace(/[^0-9]/g, '');
        let finalValue = '';
        if (numericValue) {
            const cents = parseInt(numericValue, 10);
            const dollars = cents / 100;
            finalValue = dollars.toFixed(2);
        }
        if (key === 'inPerson') {
            setInPersonPrice(finalValue);
            setConsultationPriceErrors((p) => ({ ...p, inPerson: false }));
        } else if (key === 'telemedicine') {
            setTelemedicinePrice(finalValue);
            setConsultationPriceErrors((p) => ({ ...p, telemedicine: false }));
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // The "+ Add Availability" link stays visible while a form is open. Clicking it
    // while a blank new-availability form is already open must not wipe what was typed.
    const isAddFormOpen = showForm && !editingId;

    const openAddForm = () => {
        if (isAddFormOpen) return;
        setEditingId(null);
        setFormData(emptyForm);
        setFormError('');
        setShowForm(true);
    };

    const openEditForm = (item) => {
        setEditingId(item.id);
        setFormData({
            day: item.day !== undefined && item.day !== null ? String(item.day) : '',
            start_time: (item.start_time || item.start_datetime || '').slice(0, 5),
            end_time: (item.end_time || item.end_datetime || '').slice(0, 5),
            visit_mode: visitModeFromRow(item),
            location: item.location !== undefined && item.location !== null
                ? String(item.location)
                : '',
        });
        setFormError('');
        setShowForm(true);
    };

    const handleAddLocationClick = () => {
        setLocationCountBeforeAdd(locations.length);
        setLocationFormData([{
            location_name: '',
            location_phone_no: '',
            location_address: '',
            city: '',
            state: '',
            zipCode: '',
            suite: '',
        }]);
        setSelectedLocation(null);
        setOpenLocationPopup(true);
    };

    const handleCloseLocationPopup = async () => {
        setOpenLocationPopup(false);
        const updated = await fetchLocations();
        if (updated.length > locationCountBeforeAdd) {
            const newLocation = updated[updated.length - 1];
            setFormData((prev) => ({ ...prev, location: String(newLocation.id) }));
        }
        setLocationCountBeforeAdd(0);
    };

    const cancelForm = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData(emptyForm);
        setFormError('');
    };

    const validateAvailabilityForm = () => {
        if (!formData.day) return 'Please select a day.';
        if (!formData.start_time) return 'Please select a start time.';
        if (!formData.end_time) return 'Please select an end time.';
        if (!formData.visit_mode) return 'Please select a visit mode.';
        return '';
    };

    const buildAvailabilityPayload = () => ({
        business_id: businessId,
        day: Number(formData.day),
        start_time: formData.start_time,
        end_time: formData.end_time,
        visit_mode: formData.visit_mode,
        is_telemedicine: formData.visit_mode === 'telemedicine' || formData.visit_mode === 'both',
        location: formData.location ? Number(formData.location) : null,
    });

    const submitAvailability = async () => {
        const isEdit = !!editingId;
        const url = isEdit
            ? `${process.env.REACT_APP_SERVER_URL}/api/providers/business/availability/${editingId}`
            : `${process.env.REACT_APP_SERVER_URL}/api/providers/business/availability`;
        const method = isEdit ? 'PATCH' : 'POST';

        const response = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(buildAvailabilityPayload()),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || result.error) {
            throw new Error(
                result.error || (isEdit ? 'Failed to update availability.' : 'Failed to add availability.')
            );
        }

        return { isEdit };
    };

    const handleRowDelete = async (id) => {
        if (!onDelete) return;
        await onDelete(id);
        if (editingId === id) cancelForm();
    };

    const handleDone = async () => {
        if (doneInProgressRef.current) return;
        doneInProgressRef.current = true;
        setDoneError('');
        setFormError('');

        // Require at least one consultation type only when availability is being configured
        const hasAvailability = (availabilityList || []).length > 0 || showForm;
        if (!addInPerson && !addTelemedicine && hasAvailability) {
            setTimeout(() => {
                toasterNotification(
                    'info',
                    '',
                    'Please enable either In-Person or Telemedicine consultation.',
                    8000
                );
            }, 0);
            doneInProgressRef.current = false;
            return;
        }

        // Validate prices when their checkbox is on
        const inPersonNum = inPersonPrice === '' ? NaN : parseFloat(inPersonPrice);
        const telemedicineNum = telemedicinePrice === '' ? NaN : parseFloat(telemedicinePrice);
        const inPersonInvalid = addInPerson && (isNaN(inPersonNum) || inPersonNum < 25);
        const telemedicineInvalid = addTelemedicine && (isNaN(telemedicineNum) || telemedicineNum < 25);
        if (inPersonInvalid || telemedicineInvalid) {
            setConsultationPriceErrors({ inPerson: !!inPersonInvalid, telemedicine: !!telemedicineInvalid });
            doneInProgressRef.current = false;
            return;
        }

        // If an availability form is open, validate it before saving anything
        if (showForm) {
            const availabilityError = validateAvailabilityForm();
            if (availabilityError) {
                setFormError(availabilityError);
                doneInProgressRef.current = false;
                return;
            }
        }

        if (!businessId) {
            setDoneError('Business ID is required.');
            doneInProgressRef.current = false;
            return;
        }
        if (!token) {
            setDoneError('No authentication token found.');
            doneInProgressRef.current = false;
            return;
        }

        setSavingDone(true);
        try {
            // 1. Save consultation services on the business
            const consultationPayload = {
                id: businessId,
                in_person_consultation_price: addInPerson ? Number(inPersonNum.toFixed(2)) : 0,
                telemedicine_consultation_price: addTelemedicine ? Number(telemedicineNum.toFixed(2)) : 0,
            };

            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/update-business`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(consultationPayload),
                }
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok || result.error) {
                const message = result.error || 'Failed to update consultation services.';
                setDoneError(message);
                toasterNotification('error', '', message);
                return;
            }

            const updatedBusiness = result.success && result.data ? result.data : null;
            if (updatedBusiness) {
                const merged = Array.isArray(updatedBusiness) ? updatedBusiness[0] : updatedBusiness;
                setBusinessData((prev) => ({ ...(prev || {}), ...(merged || {}) }));
                if (merged) localStorage.setItem('business', JSON.stringify({ ...(businessData || {}), ...merged }));
            } else {
                setBusinessData((prev) => ({
                    ...(prev || {}),
                    in_person_consultation_price: consultationPayload.in_person_consultation_price,
                    telemedicine_consultation_price: consultationPayload.telemedicine_consultation_price,
                }));
            }

            // 2. Save the open availability form (create or edit)
            if (showForm) {
                try {
                    const { isEdit } = await submitAvailability();
                    toasterNotification(
                        'success',
                        '',
                        isEdit ? 'Availability updated successfully.' : 'Availability added successfully.'
                    );
                    if (refreshList) await refreshList();
                } catch (availabilityErr) {
                    console.error('Error saving availability:', availabilityErr);
                    setFormError(availabilityErr.message || 'Failed to save availability.');
                    return;
                }
            } else {
                toasterNotification('success', '', 'Consultation services saved.');
            }

            if (onClose) onClose();
        } catch (err) {
            console.error('Error saving consultation services:', err);
            setDoneError('An unexpected error occurred. Please try again.');
        } finally {
            setSavingDone(false);
            doneInProgressRef.current = false;
        }
    };

    return (
        <>
        <FormModal
            open={open}
            onClose={onClose}
            title="Business Availability"
            subText="Configure default consultation services and manage availability slots for your business."
            stickyFooter={
                <StickyActionFooter
                    onCancel={onClose}
                    onSave={handleDone}
                    cancelText="Close"
                    saveText="Done"
                    isLoading={savingDone}
                    disabled={savingDone}
                />
            }
        >
            <FormStyle onSubmit={(e) => e.preventDefault()}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormHeading>Default Consultation Services</FormHeading>
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: '-8px', marginBottom: '24px' }}>
                        <Typography
                            variant="body2"
                            style={{ color: '#999', fontSize: '14px', lineHeight: '1.5', marginBottom: '8px' }}
                        >
                            Patients book these appointment types by default. Configure which appointment types your provider offers by checking the boxes below. You can also uncheck both consultation options and only use the individual services below if you don't want to enable appointment booking but want to show your services.
                        </Typography>
                        <Box
                            style={{
                                backgroundColor: '#F5F1ED',
                                padding: '12px',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                                borderRadius: '8px',
                                marginTop: '16px',
                            }}
                        >
                            <Typography
                                variant="body2"
                                style={{ color: '#79716B', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}
                            >
                                Configuration Options:
                            </Typography>
                            <Typography
                                variant="body2"
                                style={{ color: '#79716B', fontSize: '12px', lineHeight: '1.6' }}
                            >
                                • <strong>In-person only:</strong> Check In-Person, uncheck Telemedicine<br />
                                • <strong>Telemedicine only:</strong> Check Telemedicine, uncheck In-Person<br />
                                • <strong>Both:</strong> Check both options<br />
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box
                                    style={{
                                        border: '1px solid #E1E1E1',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        opacity: addInPerson ? 1 : 0.6,
                                        backgroundColor: addInPerson ? 'white' : '#FAF9F7',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: addInPerson ? '12px' : 0 }}>
                                        <div
                                            onClick={handleToggleInPerson}
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '4px',
                                                border: addInPerson ? '1px solid #75BD2F' : '1px solid #cbc1b7',
                                                backgroundColor: addInPerson ? '#75BD2F' : '#f5f1ed',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginRight: '10px',
                                                transition: 'all 0.3s ease',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {addInPerson && (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                                    <path
                                                        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                                                        fill="white"
                                                        stroke="white"
                                                        strokeWidth="1.5"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                        <FormInputLabel
                                            label={'In-Person Consultation'}
                                            labelSize={12}
                                            labelBottomMargin={'0'}
                                            style={{ margin: 0 }}
                                        />
                                    </div>
                                    {addInPerson && (
                                        <FormTextInput
                                            style={{ marginBottom: 0 }}
                                            value={inPersonPrice}
                                            label="Price"
                                            name="in_person_consultation_price"
                                            error={consultationPriceErrors.inPerson ? 'Price must be at least $25' : undefined}
                                            handleChange={(e) => handlePriceChange(e, 'inPerson')}
                                            placeholder="0.00"
                                            subText=""
                                            required={true}
                                            index={1}
                                            type="number"
                                            inputProps={{
                                                step: '0.01',
                                                min: '0',
                                                inputMode: 'decimal',
                                                style: { textAlign: 'right' },
                                                onKeyDown: (ev) => {
                                                    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
                                                    if (allowed.includes(ev.key)) return;
                                                    if (!/[0-9]/.test(ev.key)) ev.preventDefault();
                                                },
                                                onPaste: (ev) => {
                                                    const paste = (ev.clipboardData || window.clipboardData).getData('text');
                                                    if (!/^\d*$/.test(paste)) ev.preventDefault();
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box
                                                        sx={{
                                                            backgroundColor: 'rgba(203, 193, 183, 1)',
                                                            color: 'rgba(87, 83, 78, 1)',
                                                            height: '42px',
                                                            width: '42px !important',
                                                            minWidth: '42px !important',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginLeft: '-14px',
                                                            borderTopLeftRadius: '8px',
                                                            borderBottomLeftRadius: '8px',
                                                        }}
                                                    >
                                                        <AttachMoney fontSize="small" />
                                                    </Box>
                                                ),
                                                sx: {
                                                    '& input[type=number]': {
                                                        MozAppearance: 'textfield',
                                                        textAlign: 'right',
                                                        borderTopLeftRadius: '0px',
                                                        borderBottomLeftRadius: '0px',
                                                    },
                                                    '& fieldset': {
                                                        borderTopLeftRadius: '0px',
                                                        borderBottomLeftRadius: '0px',
                                                    },
                                                    '& input[type=number]::-webkit-outer-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                    '& input[type=number]::-webkit-inner-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                },
                                            }}
                                            noLabel={true}
                                            textSize={12}
                                        />
                                    )}
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box
                                    style={{
                                        border: '1px solid #E1E1E1',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        opacity: addTelemedicine ? 1 : 0.6,
                                        backgroundColor: addTelemedicine ? 'white' : '#FAF9F7',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: addTelemedicine ? '12px' : 0 }}>
                                        <div
                                            onClick={handleToggleTelemedicine}
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '4px',
                                                border: addTelemedicine ? '1px solid #75BD2F' : '1px solid #cbc1b7',
                                                backgroundColor: addTelemedicine ? '#75BD2F' : '#f5f1ed',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginRight: '10px',
                                                transition: 'all 0.3s ease',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {addTelemedicine && (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                                    <path
                                                        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                                                        fill="white"
                                                        stroke="white"
                                                        strokeWidth="1.5"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                        <FormInputLabel
                                            label={'Telemedicine Consultation'}
                                            labelSize={12}
                                            labelBottomMargin={'0'}
                                            style={{ margin: 0 }}
                                        />
                                    </div>
                                    {addTelemedicine && (
                                        <FormTextInput
                                            style={{ marginBottom: 0 }}
                                            value={telemedicinePrice}
                                            label="Price"
                                            name="telemedicine_consultation_price"
                                            error={consultationPriceErrors.telemedicine ? 'Price must be at least $25' : undefined}
                                            handleChange={(e) => handlePriceChange(e, 'telemedicine')}
                                            placeholder="0.00"
                                            subText=""
                                            required={true}
                                            index={1}
                                            type="number"
                                            inputProps={{
                                                step: '0.01',
                                                min: '0',
                                                inputMode: 'decimal',
                                                style: { textAlign: 'right' },
                                                onKeyDown: (ev) => {
                                                    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
                                                    if (allowed.includes(ev.key)) return;
                                                    if (!/[0-9]/.test(ev.key)) ev.preventDefault();
                                                },
                                                onPaste: (ev) => {
                                                    const paste = (ev.clipboardData || window.clipboardData).getData('text');
                                                    if (!/^\d*$/.test(paste)) ev.preventDefault();
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box
                                                        sx={{
                                                            backgroundColor: 'rgba(203, 193, 183, 1)',
                                                            color: 'rgba(87, 83, 78, 1)',
                                                            height: '42px',
                                                            width: '42px !important',
                                                            minWidth: '42px !important',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginLeft: '-14px',
                                                            borderTopLeftRadius: '8px',
                                                            borderBottomLeftRadius: '8px',
                                                        }}
                                                    >
                                                        <AttachMoney fontSize="small" />
                                                    </Box>
                                                ),
                                                sx: {
                                                    '& input[type=number]': {
                                                        MozAppearance: 'textfield',
                                                        textAlign: 'right',
                                                        borderTopLeftRadius: '0px',
                                                        borderBottomLeftRadius: '0px',
                                                    },
                                                    '& fieldset': {
                                                        borderTopLeftRadius: '0px',
                                                        borderBottomLeftRadius: '0px',
                                                    },
                                                    '& input[type=number]::-webkit-outer-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                    '& input[type=number]::-webkit-inner-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                },
                                            }}
                                            noLabel={true}
                                            textSize={12}
                                        />
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                        {doneError && (
                            <div style={{ color: '#D14343', fontSize: '13px', marginTop: '8px' }}>
                                {doneError}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Box
                            sx={{
                                borderBottom: '1px solid',
                                borderColor: theme.colorTableHeaderBackground,
                                paddingBottom: '10px',
                                marginTop: '24px',
                                marginBottom: '12px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            <Box
                                sx={{
                                    color: theme.secondaryFontColor,
                                    fontWeight: 700,
                                    fontSize: '18px',
                                    fontFamily: 'Poppins',
                                }}
                            >
                                Current Availabilities
                            </Box>
                            <Box className={'primary-link cursor-pointer'} onClick={openAddForm}>
                                + Add Availability
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} style={{ marginTop: '-8px', marginBottom: '16px' }}>
                        <Typography variant="body2" style={{ color: '#999', fontSize: '14px', lineHeight: '1.5' }}>
                            All availability slots configured for your business will appear here. Add a new slot to
                            allow patients to book appointments during that time window.
                        </Typography>
                    </Grid>

                    {showForm && (
                        <Grid item xs={12}>
                            <Box
                                style={{
                                    border: '1px solid #E1E1E1',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginBottom: '16px',
                                    backgroundColor: 'white',
                                }}
                            >
                                <FormInputLabel
                                    label={editingId ? 'Edit Availability' : 'New Availability'}
                                    labelSize={12}
                                    labelBottomMargin={'-mb-1'}
                                    style={{ marginBottom: '12px' }}
                                />

                                <FormSelectInput
                                    name="day"
                                    handleChange={handleFormChange}
                                    value={formData.day}
                                    required
                                    inputSize={12}
                                    placeholder={'Select Day'}
                                    options={WEEK_DAYS}
                                    optionKey="id"
                                    optionValue={['day']}
                                    noLabel
                                    style={{ marginBottom: '12px' }}
                                />

                                <div className={'flex items-center justify-between gap-4'} style={{ marginBottom: '12px' }}>
                                    <FormSelectInput
                                        name="start_time"
                                        handleChange={handleFormChange}
                                        value={formData.start_time}
                                        required
                                        inputSize={6}
                                        placeholder={'Select Start Time'}
                                        options={timeSlots}
                                        optionKey="value"
                                        optionValue={['label']}
                                        noLabel
                                    />
                                    <FormSelectInput
                                        name="end_time"
                                        handleChange={handleFormChange}
                                        value={formData.end_time}
                                        required
                                        inputSize={6}
                                        placeholder={'Select End Time'}
                                        options={timeSlots}
                                        optionKey="value"
                                        optionValue={['label']}
                                        noLabel
                                    />
                                </div>

                                <FormSelectInput
                                    name="location"
                                    handleChange={handleFormChange}
                                    value={formData.location}
                                    required={false}
                                    inputSize={12}
                                    placeholder={loadingLocations ? 'Loading locations...' : 'Select Location'}
                                    options={locations}
                                    optionKey="id"
                                    optionValue={['location']}
                                    noLabel
                                    style={{ marginBottom: '12px' }}
                                    showAddOption={true}
                                    addOptionText="+ Add a Location"
                                    onAddOptionClick={handleAddLocationClick}
                                />

                                <FormSelectInput
                                    name="visit_mode"
                                    handleChange={handleFormChange}
                                    value={formData.visit_mode}
                                    required
                                    inputSize={12}
                                    placeholder={'Select Visit Mode'}
                                    options={VISIT_MODE_OPTIONS}
                                    optionKey="id"
                                    optionValue={['day']}
                                    className="long-label"
                                    noLabel
                                />

                                {formError && (
                                    <div style={{ color: '#D14343', fontSize: '13px', marginTop: '8px' }}>
                                        {formError}
                                    </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px', marginTop: '16px' }}>
                                    <div>
                                        {editingId && (
                                            <Box
                                                className={'danger-link cursor-pointer'}
                                                onClick={savingDone ? undefined : () => handleRowDelete(editingId)}
                                                style={{ opacity: savingDone ? 0.6 : 1, fontSize: '13px' }}
                                            >
                                                Delete Availability
                                            </Box>
                                        )}
                                    </div>
                                    <Box
                                        onClick={cancelForm}
                                        style={{
                                            cursor: 'pointer',
                                            color: '#79716B',
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: '13px',
                                        }}
                                    >
                                        Cancel
                                    </Box>
                                </div>
                            </Box>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        {(() => {
                            const visibleList = (availabilityList || []).filter(
                                (item) => !editingId || item?.id !== editingId
                            );
                            if (visibleList.length === 0) {
                                return (
                                    <Box
                                        style={{
                                            border: '1px dashed #E1E1E1',
                                            borderRadius: '8px',
                                            padding: '32px 16px',
                                            textAlign: 'center',
                                            backgroundColor: '#FAF9F7',
                                            color: '#79716B',
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: '14px',
                                        }}
                                    >
                                        No availabilities added yet.
                                    </Box>
                                );
                            }
                            return visibleList.map((item, index) => (
                                <Box
                                    key={item?.id ?? index}
                                    style={{
                                        border: '1px solid #E1E1E1',
                                        borderRadius: '8px',
                                        padding: '14px 16px',
                                        marginBottom: '12px',
                                        backgroundColor: 'white',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontSize: '14px',
                                        color: '#3E3E4A',
                                        gap: '12px',
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                                        <div>
                                            <strong>{dayLabelFromValue(item?.day)}</strong>
                                            <span style={{ marginLeft: '12px', color: '#79716B' }}>
                                                {formatTimeLabel(item?.start_time || item?.start_datetime)} -{' '}
                                                {formatTimeLabel(item?.end_time || item?.end_datetime)}
                                            </span>
                                        </div>
                                        <span style={{ color: '#79716B', fontSize: '13px' }}>
                                            {visitModeLabel(visitModeFromRow(item))}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <Box
                                            className={'primary-link cursor-pointer'}
                                            onClick={() => openEditForm(item)}
                                            style={{ fontSize: '13px' }}
                                        >
                                            Edit
                                        </Box>
                                        <Box
                                            className={'danger-link cursor-pointer'}
                                            onClick={() => handleRowDelete(item.id)}
                                            style={{ fontSize: '13px' }}
                                        >
                                            Delete
                                        </Box>
                                    </div>
                                </Box>
                            ));
                        })()}
                    </Grid>

                    <Grid item xs={12}>
                        <div style={{ height: '24px' }} />
                    </Grid>
                </Grid>
            </FormStyle>
        </FormModal>
        <AddLocationPopup
            open={openLocationPopup}
            onClose={handleCloseLocationPopup}
        />
        </>
    );
};

export default AvailabilityPopup;
